import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    RewardsAvailableRewardsB2bPageWidgetDefinition,
    RewardsAvailableRewardsPageWidgetDefinition,
} from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { parseOptionalBooleanDataAttr } from "@ui-utils/Injector/utils";

import RewardsAvailableRewardsB2bPageWidget from "./b2b/RewardsAvailableRewardsB2bPageWidget";
import RewardsAvailableRewardsPageWidget from "./RewardsAvailableRewardsPageWidget";

interface RewardsAvailableRewardsPageWidgetInjectorConfig
    extends RewardsWidgetInjectorConfig {
    isCustomerB2b: boolean;
}
class RewardsAvailableRewardsPageWidgetInjector {
    constructor(
        private readonly config: RewardsAvailableRewardsPageWidgetInjectorConfig
    ) {}

    start(): void {
        this.config.logger.debug(
            "RewardsAvailableRewardsPageWidgetInjector start",
            {
                placeholderSelector: this.config.placeholderSelector,
            }
        );

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.config.logger?.debug(
                "RewardsAvailableRewardsPageWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            let widget:
                | RewardsAvailableRewardsPageWidget
                | RewardsAvailableRewardsB2bPageWidget;
            let widgetName: REWARDS_WIDGET_NAMES;

            void (async () => {
                if (this.config.isCustomerB2b) {
                    const RewardsAvailableRewardsB2bPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsAvailableRewardsB2bPageWidgetDefinition,
                            true
                        );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_AVAILABLE_REWARDS_B2B_PAGE_WIDGET;
                    widget = new RewardsAvailableRewardsB2bPageWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                        },
                        this.config.logger
                    );
                } else {
                    const RewardsAvailableRewardsPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsAvailableRewardsPageWidgetDefinition,
                            true
                        );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_AVAILABLE_REWARDS_PAGE_WIDGET;
                    widget = new RewardsAvailableRewardsPageWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                        },
                        this.config.logger
                    );
                }

                await widget.init();

                this.config.eventsCollector.pushEvent(
                    this.config.eventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default RewardsAvailableRewardsPageWidgetInjector;
